import Button from '@/components/Button';
import { ButtonVariant } from '@/components/ButtonVariant';
import { Icon, IconSize, IconVariant } from '@/components/Icon';
import Video from '@/components/Video';
import { GameConfig } from '@/config/game';
import { OddsVariant } from '@/config/odds/OddsVariant';
import { WinningNumbersData } from '@/config/odds/winningNumbers';
import { videoConfig } from '@/config/video';
import { fetchWinningNumbers } from '@/helpers/fetchWinningNumbers';
import { getGameByShortname } from '@/helpers/getGameByShortname';
import { formatDate, fromTimestamp } from '@/utilities/dateUtils';
import formDrawVideoDescription from '@/utilities/formDrawVideoDescription';
import Link from 'next/link';
import React from 'react';
import { LinkType } from './Link';
import TextToggle from './TextToggle';

export enum DrawVideoVariant {
  Lotto = 'lotto',
  Eurojackpot = 'eurojackpot',
  Keno = 'keno',
}

export interface DrawVideoProps {
  variant: DrawVideoVariant;
  winningNumbersData?: WinningNumbersData;
  date?: string | number;
  drawData?: any;
}

interface DrawVideoState {
  variant: DrawVideoVariant;
  is360: boolean;
  youTubeData?: YouTubeData;
  description?: string;
}

interface YouTubeData {
  [game: string]: {
    [date: string]: string;
  };
}

const makeDateFromString = (dateString: string): number => {
  const [dd, mm, yyyy] = dateString.split('.');
  return new Date(`${yyyy}/${mm}/${dd}`).getTime();
};
const findNewestDate = (dateStrings: string[]): string => {
  try {
    const dates = dateStrings.map((date) => {
      return makeDateFromString(date);
    });
    const now = new Date().getTime();
    const pastDates = dates.filter((date) => date < now);
    const newestDate = Math.max(...pastDates);
    return Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(
      newestDate
    );
  } catch {
    return Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(
      new Date()
    );
  }
};

class DrawVideo extends React.Component<DrawVideoProps, DrawVideoState> {
  private gameConfig: GameConfig | undefined;
  private oddsVariant: OddsVariant | undefined;
  private drawData: any;
  constructor(props) {
    super(props);
    this.oddsVariant =
      this.props.variant === DrawVideoVariant.Lotto
        ? OddsVariant.Lotto6aus49
        : this.props.variant === DrawVideoVariant.Eurojackpot
          ? OddsVariant.Eurojackpot
          : this.props.variant === DrawVideoVariant.Keno
            ? OddsVariant.Keno
            : undefined;
    this.gameConfig = getGameByShortname(
      this.props.variant === 'lotto' ? 'lotto6aus49' : this.props.variant
    );
    if (this.props.drawData) {
      this.drawData = this.props.drawData;
    }
    this.state = {
      variant: this.props.variant,
      is360: false,
      description: formDrawVideoDescription(this.drawData, this.props.variant),
    };
  }

  private async fetchData(): Promise<void> {
    if (this.gameConfig && this.oddsVariant) {
      try {
        let winningNumbers: any = await fetchWinningNumbers(
          this.oddsVariant,
          this.gameConfig,
          +fromTimestamp(makeDateFromString(this.date)),
          false
        );
        if (winningNumbers) {
          this.drawData = winningNumbers;
          this.setState({
            description: formDrawVideoDescription(
              this.drawData,
              this.props.variant + (this.state.is360 ? '360' : '')
            ),
          });
        } else if (this.oddsVariant === OddsVariant.Lotto6aus49) {
          // Sonderfall am Ziehungstag: ohne Datum aufrufen!
          winningNumbers = await fetchWinningNumbers(
            this.oddsVariant,
            this.gameConfig
          );
          if (winningNumbers) {
            this.drawData = winningNumbers;
            this.setState({
              description: formDrawVideoDescription(
                this.drawData,
                this.props.variant
              ),
            });
          }
        }
      } catch {
        //console.error('WinningNumbersContainer - fetchData failed', error);
      }
    }
  }

  async componentDidMount() {
    const response = await fetch(
      `/api/data/youtube?game=${this.state.variant}`
    );
    if (response.ok) {
      this.setState({ youTubeData: await response.json() });
      if (this.oddsVariant) this.fetchData().then();
    }
  }

  private findVideo = (
    variant: DrawVideoVariant = this.state.variant
  ): string | undefined => {
    const { youTubeData } = this.state;
    if (youTubeData) {
      const videos = youTubeData[variant] || {};
      return videos[this.date];
    }
    return undefined;
  };

  private findVideoId = (
    variant: DrawVideoVariant = this.state.variant
  ): string | undefined => {
    return this.findVideo(variant);
  };

  private get availableDates(): string[] {
    const { youTubeData } = this.state;
    const { variant } = this.state;
    if (youTubeData) {
      return Object.keys(youTubeData[variant] || {});
    }
    return [];
  }

  private get has360(): boolean {
    const variant360 = (this.props.variant + '360') as DrawVideoVariant;
    return this.findVideoId(variant360) !== undefined;
  }

  private get date(): string {
    if (this.props.date) {
      return formatDate(this.props.date, 'Default');
    } else if (this.props.winningNumbersData && this.config) {
      return this.winningNumbersDate;
    }
    return this.fallbackDate;
  }

  private get winningNumbersDate(): string {
    if (this.props.winningNumbersData && this.config) {
      const id = this.config.id;
      const data = this.props.winningNumbersData[id.toString()];
      if (data) {
        const item = Array.isArray(data)
          ? data.sort((a, b) => {
              return !!a && !!b ? (b.drawDate || 0) - (a.drawDate || 0) : 0;
            })[0]
          : data;
        const drawDate = item.drawDate;
        return drawDate ? formatDate(drawDate, 'Default') : this.fallbackDate;
      }
      return this.fallbackDate;
    }
    return this.fallbackDate;
  }

  private get fallbackDate(): string {
    return findNewestDate(this.availableDates);
  }

  private get360Url(): string {
    const videoId = this.findVideoId(
      (this.props.variant + '360') as DrawVideoVariant
    );
    if (this.has360) return videoId ? videoConfig.baseUrl + videoId : '#';
    return '#';
  }
  private get url(): string | null {
    const videoId = this.findVideoId();
    return videoId ? videoConfig.baseUrl + videoId : null;
  }

  private get config(): GameConfig | undefined {
    return getGameByShortname(this.state.variant);
  }

  private get titleStart(): string {
    const variant = this.props.variant;
    switch (variant) {
      case DrawVideoVariant.Lotto: {
        return 'Die Ziehung der Lottozahlen';
      }
      case DrawVideoVariant.Eurojackpot: {
        return 'Die Eurojackpot Ziehung';
      }
      case DrawVideoVariant.Keno: {
        return 'Die Ziehung der KENO Zahlen';
      }
      default: {
        return 'Die Ziehung';
      }
    }
  }

  private get title(): string {
    return (
      `${this.titleStart} vom ${this.date}` +
      (this.state.is360 ? ` in 360°` : ``)
    );
  }

  private get buttonLabel(): string {
    if (this.state.is360) {
      return 'Normales Video ansehen';
    } else {
      if (process.env.NEXT_PUBLIC_EMBED_360_VIDEOS === 'true') {
        return '360° Video ansehen';
      }
      return '360° Video bei YouTube ansehen';
    }
  }

  private getSwappedVariant = (variant: DrawVideoVariant): DrawVideoVariant => {
    if (variant.indexOf('360') < 0) {
      return (variant + '360') as DrawVideoVariant;
    } else {
      return variant.replace('360', '') as DrawVideoVariant;
    }
  };

  private handleOnClick = (): void => {
    this.setState((oldState) => {
      this.fetchData().then();
      return {
        variant: this.getSwappedVariant(oldState.variant),
        is360: !oldState.is360,
      };
    });
  };

  componentDidUpdate(prevProps: DrawVideoProps) {
    if (prevProps.variant !== this.props.variant) {
      this.setState({
        variant: this.props.variant,
        is360: false,
      });
    }
    if (prevProps.date !== this.props.date && this.oddsVariant) {
      this.fetchData().then();
    }
  }

  render(): JSX.Element | null {
    const url = this.url;
    if (url) {
      return this.has360 ? (
        <div className="DrawVideo Video Video--boxed">
          <div className="DrawVideo__video">
            <Video
              url={url}
              is360={this.state.is360}
              variant={this.state.variant}
            />
          </div>
          <div className="Video__description DrawVideo__description">
            <div className="DrawVideo__title">
              <figcaption className="Video__description">
                <span>{this.title}</span>
                <TextToggle triggerText="Volltext-Version">
                  <div
                    className="full-width-content"
                    dangerouslySetInnerHTML={{
                      __html: this.state.description || '',
                    }}
                  />
                </TextToggle>
              </figcaption>
            </div>

            {this.has360 &&
              process.env.NEXT_PUBLIC_EMBED_360_VIDEOS === 'true' && (
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={this.handleOnClick}
                >
                  <span>{this.buttonLabel}</span>
                  <Icon variant={IconVariant.ArrowRight} />
                </Button>
              )}
            {this.has360 &&
              process.env.NEXT_PUBLIC_EMBED_360_VIDEOS !== 'true' && (
                <Link
                  type={LinkType.External}
                  href={this.get360Url()}
                  className={
                    'Link Link--button Link--external Button--primary Button--big Button--auto-height'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{this.buttonLabel}</span>
                  <Icon
                    variant={IconVariant.External}
                    size={IconSize.Small}
                    className={'Icon--spaced'}
                  />
                </Link>
              )}
          </div>
        </div>
      ) : (
        <Video
          url={url}
          is360={false}
          title={this.title}
          boxed={true}
          variant={this.state.variant}
          volltextVersion={this.state.description}
        />
      );
    } else if (this.winningNumbersDate !== this.fallbackDate) {
      return (
        <div className="Video Video--boxed">
          <span className="Video__notice">
            Das aktuelle Ziehungsvideo steht Ihnen in Kürze zur Verfügung
          </span>
        </div>
      );
    }
    return null;
  }
}

export default DrawVideo;
